import { useEffect, useId, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

const navLinkClass = ({ isActive }) =>
  [
    "font-sans text-xs font-bold tracking-[0.1em] pb-1 transition-all duration-300",
    isActive
      ? "text-[#8B1A2B] border-b-2 border-[#8B1A2B]"
      : "text-[#343022] opacity-60 hover:opacity-100 hover:text-[#8B1A2B]",
  ].join(" ");

const mobileNavLinkClass = ({ isActive }) =>
  [
    "block border-b border-[#8B1A2B]/15 py-4 font-sans text-sm font-bold uppercase tracking-[0.15em] transition-colors",
    isActive
      ? "text-[#8B1A2B]"
      : "text-[#343022] active:bg-[#A8C8E8]/20",
  ].join(" ");

export default function SiteLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const menuId = useId();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  return (
    <>
      <header className="fixed top-0 z-[60] w-full bg-[#F5EDD8]/70 shadow-[0_20px_40px_rgba(52,48,34,0.06)] backdrop-blur-xl dark:bg-[#1a1a1a]/70">
        <nav
          className="flex w-full max-w-none items-center justify-between gap-3 px-6 py-6 md:gap-6 md:px-12 md:py-8"
          aria-label="Main"
        >
          <Link
            to="/"
            className="min-w-0 shrink font-serif text-xl font-light tracking-widest text-[#8B1A2B] dark:text-[#E8DCC4] md:text-3xl"
            onClick={() => setMenuOpen(false)}
          >
            SOFIA &amp; JASON
          </Link>
          <div className="hidden items-center gap-12 md:flex">
            <NavLink to="/" end className={navLinkClass}>
              HOME
            </NavLink>
            <NavLink to="/about" className={navLinkClass}>
              ABOUT
            </NavLink>
            <NavLink to="/details" className={navLinkClass}>
              DETAILS &amp; RSVP
            </NavLink>
            <NavLink to="/travel" className={navLinkClass}>
              TRAVEL &amp; FAQ
            </NavLink>
            <NavLink to="/registry" className={navLinkClass}>
              REGISTRY
            </NavLink>
          </div>
          <div className="flex shrink-0 items-center gap-2 md:gap-0">
            <button
              type="button"
              className="flex h-11 w-11 flex-col items-center justify-center gap-1.5 rounded-none border-2 border-[#8B1A2B]/25 bg-transparent md:hidden"
              aria-expanded={menuOpen}
              aria-controls={menuId}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <span
                className={`block h-0.5 w-5 origin-center bg-[#8B1A2B] transition-transform duration-200 ${menuOpen ? "translate-y-2 rotate-45" : ""}`}
              />
              <span
                className={`block h-0.5 w-5 bg-[#8B1A2B] transition-opacity duration-200 ${menuOpen ? "opacity-0" : ""}`}
              />
              <span
                className={`block h-0.5 w-5 origin-center bg-[#8B1A2B] transition-transform duration-200 ${menuOpen ? "-translate-y-2 -rotate-45" : ""}`}
              />
            </button>
            <Link
              to="/details#rsvp"
              className="scale-95 bg-primary px-4 py-2.5 font-sans text-xs font-bold uppercase tracking-[0.1em] text-on-primary transition-all duration-200 ease-in-out hover:bg-primary-container md:ml-4 md:px-8 md:py-3"
            >
              RSVP
            </Link>
          </div>
        </nav>
      </header>

      {menuOpen ? (
        <div
          id={menuId}
          className="fixed inset-0 z-[55] flex flex-col bg-[#F5EDD8]/98 backdrop-blur-xl md:hidden"
          style={{ paddingTop: "5.25rem", paddingBottom: "env(safe-area-inset-bottom, 0)" }}
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
        >
          <div className="flex flex-1 flex-col overflow-y-auto px-6 pb-8">
            <NavLink to="/" end className={mobileNavLinkClass} onClick={() => setMenuOpen(false)}>
              Home
            </NavLink>
            <NavLink
              to="/about"
              className={mobileNavLinkClass}
              onClick={() => setMenuOpen(false)}
            >
              About
            </NavLink>
            <NavLink
              to="/details"
              className={mobileNavLinkClass}
              onClick={() => setMenuOpen(false)}
            >
              Details &amp; RSVP
            </NavLink>
            <NavLink
              to="/travel"
              className={mobileNavLinkClass}
              onClick={() => setMenuOpen(false)}
            >
              Travel &amp; FAQ
            </NavLink>
            <NavLink
              to="/registry"
              className={mobileNavLinkClass}
              onClick={() => setMenuOpen(false)}
            >
              Registry
            </NavLink>
          </div>
        </div>
      ) : null}

      <Outlet />
      <footer className="w-full bg-[#A8C8E8] px-6 py-12 md:px-12 dark:bg-[#A8C8E8]">
        <div className="flex w-full flex-col items-start justify-between gap-8 md:flex-row">
          <div className="font-['Plus_Jakarta_Sans'] text-xs text-[#343022] dark:text-[#343022]">
            <div className="uppercase tracking-widest">
              © 2026 Made with ❤️ by Jason
            </div>
            <div className="mt-1 text-[3px] normal-case tracking-normal">
              (He&apos;s the sweetest)
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
